import { useState } from 'react'
import { Eye, EyeOff, Moon, Sun } from 'lucide-react'

import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { FormField } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { persistAuthAndRedirect } from '@/lib/auth'
import { AuthService } from '@/services/authService'
import { encodeBase64Utf8 } from '@/utils/data'
import { showToastMessage } from '@/utils'
import {
  assertPortalRolesPresent,
  getAuthErrorStatus,
  getLoginErrorMessage,
  normalizeMobile,
} from './loginUtils'

function AuthThemeToggle() {
  const [dark, setDark] = useState(() => document.documentElement.dataset.theme === 'dark')
  const toggle = () => {
    const next = !dark
    document.documentElement.dataset.theme = next ? 'dark' : 'light'
    try {
      localStorage.setItem('panel.theme', next ? 'dark' : 'light')
    } catch {
      /* storage unavailable; theme still applies for this visit */
    }
    setDark(next)
  }
  const label = `Switch to ${dark ? 'light' : 'dark'} theme`
  return (
    <Button variant="ghost" size="icon" onClick={toggle} title={label} aria-label={label}>
      {dark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
    </Button>
  )
}

export default function LoginPage() {
  const [mobileInput, setMobileInput] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [showForgot, setShowForgot] = useState(false)

  const mobile = normalizeMobile(mobileInput)
  const canSubmit = mobile.length === 10 && password.length > 0

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!canSubmit || loading) return
    setLoading(true)
    setError('')

    try {
      const data = await AuthService.phoneLogin({ mobile, password: encodeBase64Utf8(password) })
      assertPortalRolesPresent(data)
      persistAuthAndRedirect(data)
    } catch (err) {
      if (getAuthErrorStatus(err) === 429) {
        showToastMessage('Too many attempts, try again later.', false)
        setError('Too many attempts, try again later.')
      } else {
        if (getAuthErrorStatus(err) === 400) console.error('Auth API rejected the request. Verify the password is base64 encoded.', err)
        setError(getLoginErrorMessage(err))
      }
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-bg">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-surface px-4 sm:px-6">
        <div className="flex items-center gap-2">
          <div className="flex size-6 items-center justify-center rounded-sm bg-primary font-semibold text-primary-on">OP</div>
          <span className="font-semibold text-text">Challan Fulfilment</span>
        </div>
        <AuthThemeToggle />
      </header>

      <main className="relative flex flex-1 items-center justify-center px-4 py-8">
        {/* Flat dot grid, decorative only. */}
        <svg aria-hidden="true" className="pointer-events-none absolute inset-0 size-full fill-border">
          <defs>
            <pattern id="login-dots" width="20" height="20" patternUnits="userSpaceOnUse">
              <rect width="2" height="2" x="9" y="9" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#login-dots)" />
        </svg>

        <form onSubmit={handleSubmit} noValidate className="relative flex w-full max-w-[360px] flex-col gap-4 rounded-lg border border-border bg-surface p-6">
          <div>
            <h1 className="text-lg font-semibold text-text">Sign in</h1>
            <p className="mt-1 text-text-secondary">Enter your phone number and password.</p>
          </div>

          <FormField id="mobile" label="Phone number" required>
            {(controlProps) => (
              <div className="flex">
                <span className="flex items-center rounded-l-md border border-r-0 border-border-strong bg-surface-sunken px-3 text-text-secondary">+91</span>
                <Input
                  {...controlProps}
                  name="tel"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  enterKeyHint="next"
                  placeholder="10-digit number"
                  value={mobileInput}
                  disabled={loading}
                  required
                  className="rounded-l-none"
                  onChange={(event) => setMobileInput(normalizeMobile(event.target.value))}
                />
              </div>
            )}
          </FormField>

          <div className="flex flex-col gap-1">
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="font-medium text-text">
                Password <span aria-hidden="true" className="text-error">*</span>
              </label>
              <button
                type="button"
                className="rounded-sm text-primary hover:underline focus-visible:outline-none focus-visible:shadow-focus"
                aria-expanded={showForgot}
                onClick={() => setShowForgot((value) => !value)}
              >
                Forgot password?
              </button>
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                disabled={loading}
                required
                aria-required="true"
                placeholder="Enter password"
                onChange={(event) => setPassword(event.target.value)}
                className="pr-11"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-md text-text-secondary hover:text-text focus-visible:outline-none focus-visible:shadow-focus"
                onClick={() => setShowPassword((value) => !value)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
              >
                {showPassword ? <EyeOff className="size-4" aria-hidden="true" /> : <Eye className="size-4" aria-hidden="true" />}
              </button>
            </div>
          </div>

          {showForgot ? <Alert tone="info">Ask your manager or admin to reset your password.</Alert> : null}
          {error ? <Alert tone="error">{error}</Alert> : null}

          <Button type="submit" loading={loading} disabled={!canSubmit} className="w-full">
            {loading ? 'Signing in' : 'Sign in'}
          </Button>
        </form>
      </main>
    </div>
  )
}
